import { useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { useLocalStorage } from "@src/appV2/lib/utils/useLocalstorage";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useState } from "react";
import Confetti from "react-confetti";

import {
  getScholarshipStorageKey,
  SCHOLARSHIP_RESPONSES,
  type ScholarshipResponse,
} from "./constants";
import { ScholarshipBanner } from "./ScholarshipBanner";
import { ScholarshipBottomSheet } from "./ScholarshipBottomSheet";

export function ScholarshipEntrypoint() {
  const bottomSheetModalState = useModalState();
  const { userId } = useDefinedWorker();
  const { showInfoToast } = useToast();
  const [showConfetti, setShowConfetti] = useState(false);

  const isEnabled = useCbhFlag(CbhFeatureFlag.CONCORDE_SCHOLARSHIP, {
    defaultValue: false,
  });

  const storageKey = getScholarshipStorageKey(userId);

  const [scholarshipResponse, setScholarshipResponse] = useLocalStorage<
    ScholarshipResponse | undefined
  >(storageKey, undefined);

  const shouldShowBanner = isEnabled && !isDefined(scholarshipResponse);

  useLogEffect(
    APP_V2_USER_EVENTS.CONCORDE_SCHOLARSHIP_BANNER_VIEWED,
    { workerId: userId },
    { enabled: shouldShowBanner }
  );

  const handleBannerClick = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.CONCORDE_SCHOLARSHIP_BANNER_CTA_CLICKED, {
      workerId: userId,
    });
    bottomSheetModalState.openModal();
  }, [userId, bottomSheetModalState]);

  const handleSubmit = useCallback(
    (response: ScholarshipResponse) => {
      setScholarshipResponse(response);

      showInfoToast("Thanks for letting us know!");

      if (response === SCHOLARSHIP_RESPONSES.INTERESTED) {
        setShowConfetti(true);
      }
    },
    [setScholarshipResponse, showInfoToast]
  );

  if (!shouldShowBanner && !showConfetti) {
    return null;
  }

  return (
    <>
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          numberOfPieces={400}
          gravity={0.8}
          recycle={false}
          onConfettiComplete={() => {
            setShowConfetti(false);
          }}
        />
      )}
      {shouldShowBanner && (
        <>
          <ScholarshipBanner onClick={handleBannerClick} />
          <ScholarshipBottomSheet
            modalState={bottomSheetModalState}
            workerId={userId}
            onSubmit={handleSubmit}
          />
        </>
      )}
    </>
  );
}
